/**
 * Pure helpers for normalizing and structuring explanation text.
 * Used by MCQ EnhancedExplanation and Flashcard answer reveal.
 */

/** Strip common markdown syntax so the user only ever sees plain prose. */
export function stripMarkdown(input: string): string {
  if (!input) return "";
  let s = input;

  s = s.replace(/```[\s\S]*?```/g, (m) =>
    m.replace(/```[a-zA-Z0-9]*\n?|```/g, "")
  );
  s = s.replace(/`([^`]+)`/g, "$1");

  s = s.replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1");
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");

  s = s.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  s = s.replace(/^\s{0,3}>\s?/gm, "");
  s = s.replace(/^\s*([-*_])\1{2,}\s*$/gm, "");

  s = s.replace(/^\s*[-*+]\s+/gm, "");
  s = s.replace(/^\s*\d+\.\s+/gm, "");

  s = s.replace(/\*\*\*([^*]+)\*\*\*/g, "$1");
  s = s.replace(/___([^_]+)___/g, "$1");
  s = s.replace(/\*\*([^*]+)\*\*/g, "$1");
  s = s.replace(/__([^_]+)__/g, "$1");
  s = s.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "$1");
  s = s.replace(/(?<!_)_([^_\n]+)_(?!_)/g, "$1");
  s = s.replace(/~~([^~]+)~~/g, "$1");

  s = s.replace(/^\s*\|.*\|\s*$/gm, (line) =>
    line.replace(/\|/g, " ").replace(/\s{2,}/g, "  ").trim()
  );
  s = s.replace(/^\s*[:\- ]+\s*$/gm, "");

  s = s.replace(/[ \t]+\n/g, "\n");
  s = s.replace(/\n{3,}/g, "\n\n");

  return s.trim();
}

export type SectionKind = "correct" | "incorrect" | "pearl" | "mechanism";

export interface ParsedSection {
  id: string;
  kind: SectionKind;
  title: string;
  paragraphs: string[];
}

export interface ParsedLead {
  id: string;
  text: string;
}

export interface ParsedBodyParagraph {
  id: string;
  text: string;
}

export interface ParsedExplanation {
  lead: ParsedLead | null;
  sections: ParsedSection[];
  body: ParsedBodyParagraph[];
  /** Total cleaned-text length, useful for "is long" heuristics. */
  totalLength: number;
}

interface CueDef {
  kind: SectionKind;
  title: string;
  /** Matches at the START of a paragraph (case-insensitive). */
  pattern: RegExp;
}

const CUES: CueDef[] = [
  {
    kind: "correct",
    title: "Why this is correct",
    pattern:
      /^(why\s+(this|the\s+answer|[a-e])\s+is\s+correct|correct\s+because|the\s+correct\s+answer\s+is|this\s+(answer\s+)?is\s+correct)\b[:.\s-]*/i,
  },
  {
    kind: "incorrect",
    title: "Why the others are wrong",
    pattern:
      /^(why\s+(the\s+)?(others?|other\s+options?)\s+(are|is)\s+(wrong|incorrect)|other\s+options?|distractors?|incorrect\s+options?)\b[:.\s-]*/i,
  },
  {
    kind: "pearl",
    title: "Clinical pearl",
    pattern:
      /^(clinical\s+pearl|pearl|key\s+point|key\s+takeaway|remember|high[-\s]?yield|takeaway)\b[:.\s-]*/i,
  },
  {
    kind: "mechanism",
    title: "Mechanism",
    pattern:
      /^(mechanism|pathophysiology|background|how\s+it\s+works)\b[:.\s-]*/i,
  },
];

function extractLead(firstParagraph: string): { lead: string; rest: string } {
  // Take up to the first sentence terminator; cap at ~220 chars.
  const match = firstParagraph.match(/^(.{20,220}?[.!?])(\s+|$)/s);
  if (match) {
    const lead = match[1].trim();
    const rest = firstParagraph.slice(match[0].length).trim();
    return { lead, rest };
  }
  // Fallback: short paragraph IS the lead.
  if (firstParagraph.length <= 240) {
    return { lead: firstParagraph.trim(), rest: "" };
  }
  // Hard cap.
  return {
    lead: firstParagraph.slice(0, 220).trim() + "…",
    rest: firstParagraph.slice(220).trim(),
  };
}

/**
 * Parse cleaned explanation text into a lead + structured sections + remaining body.
 * Section detection is heuristic and silent: if no cues match, sections is [] and
 * everything (after the lead) goes into `body`.
 */
export function parseExplanation(rawText: string): ParsedExplanation {
  const cleaned = stripMarkdown(rawText || "");
  if (!cleaned) {
    return { lead: null, sections: [], body: [], totalLength: 0 };
  }

  const paragraphs = cleaned
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) {
    return { lead: null, sections: [], body: [], totalLength: cleaned.length };
  }

  // Lead from first paragraph.
  const { lead, rest } = extractLead(paragraphs[0]);
  const remaining: string[] = [];
  if (rest) remaining.push(rest);
  for (let i = 1; i < paragraphs.length; i++) remaining.push(paragraphs[i]);

  // Walk remaining paragraphs, attaching to sections when cues match.
  const sections: ParsedSection[] = [];
  const body: ParsedBodyParagraph[] = [];
  let current: ParsedSection | null = null;
  let sectionIndex = 0;
  let bodyIndex = 0;

  for (const para of remaining) {
    let matched: CueDef | null = null;
    let stripped = para;

    for (const cue of CUES) {
      const m = para.match(cue.pattern);
      if (m) {
        matched = cue;
        stripped = para.slice(m[0].length).trim();
        break;
      }
    }

    if (matched) {
      current = {
        id: `sec:${sectionIndex++}`,
        kind: matched.kind,
        title: matched.title,
        paragraphs: [],
      };
      sections.push(current);
      if (stripped) current.paragraphs.push(stripped);
    } else if (current) {
      current.paragraphs.push(para);
    } else {
      body.push({ id: `body:${bodyIndex++}`, text: para });
    }
  }

  // Drop empty sections (cue with no content and no follow-up).
  const filteredSections = sections.filter((s) => s.paragraphs.length > 0);

  return {
    lead: lead ? { id: "lead", text: lead } : null,
    sections: filteredSections,
    body,
    totalLength: cleaned.length,
  };
}

/* ---------------- Search helpers ---------------- */

import type { ReactNode } from "react";
import { createElement, Fragment } from "react";

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Minimum query length for search to be active. */
export const SEARCH_MIN_LEN = 2;

/** Count case-insensitive occurrences of `query` in `text`. */
export function countMatches(text: string, query: string): number {
  if (!text || !query || query.length < SEARCH_MIN_LEN) return 0;
  const re = new RegExp(escapeRegExp(query), "gi");
  const m = text.match(re);
  return m ? m.length : 0;
}

/**
 * Split `text` on `query` (case-insensitive) and return a React node where
 * each match is wrapped in a <mark className="explanation-match">.
 * If query is too short or no matches, returns the plain text.
 */
export function highlightMatches(text: string, query: string): ReactNode {
  if (!text) return text;
  if (!query || query.length < SEARCH_MIN_LEN) return text;
  const re = new RegExp(`(${escapeRegExp(query)})`, "gi");
  const parts = text.split(re);
  if (parts.length === 1) return text;
  const nodes: ReactNode[] = parts.map((part, i) => {
    if (i % 2 === 1) {
      return createElement(
        "mark",
        { key: i, className: "explanation-match" },
        part
      );
    }
    return part;
  });
  return createElement(Fragment, null, ...nodes);
}
